import { postAndAppendScript } from "../shared/requests";

const FALLBACK_SUBCATEGORY_MAJORS = ["Fish", "Shellfish", "Tree nuts", "Gluten"];
const WARN_UNSAVED_ATTR = "data-warn-unsaved-allergens";
const INCOMPLETE_SETTLE_MS = 1200;
const UNSAVED_INCOMPLETE_MESSAGE =
  "You have allergens without a type selected. If you continue, those selections will be lost.";

let subcategoryMajorsCache = null;
let unsavedMarker = null;
let unsavedPartialReplaceBound = false;

export class AllergenInput {
  constructor(container, onUpdate, { warnUnsavedChanges = false } = {}) {
    this.container = container;
    this.onUpdate = onUpdate;
    this.warnUnsavedChanges = warnUnsavedChanges;

    this.bind();
  }

  // Warn after each autosave, since the save silently drops bare majors.
  static bindAutosave(container, save) {
    const input = new AllergenInput(
      container,
      (allergens) => {
        save(allergens);
        AllergenInput.showIncompleteWarning(container);
      },
      { warnUnsavedChanges: true }
    );

    AllergenInput.bindSettleWarning(container);

    return input;
  }

  // A bare major never fires onUpdate, so without this it stays hidden until save/submit. The settle
  // delay lets a subtype picked right after the major clear it first, so the warning doesn't flash.
  static bindSettleWarning(container) {
    let settleTimer;
    container.addEventListener("change", () => {
      clearTimeout(settleTimer);
      if (AllergenInput.warningVisible(container)) return;
      if (AllergenInput.incompleteMajors(container).length === 0) return;
      settleTimer = setTimeout(
        () => AllergenInput.showIncompleteWarning(container),
        INCOMPLETE_SETTLE_MS
      );
    });
  }

  bind() {
    this.container.querySelectorAll(".allergen-checkbox").forEach((checkbox) => {
      checkbox.addEventListener("change", this.onCheckboxChange.bind(this));
    });

    this.container.querySelectorAll(".other-checkbox").forEach((checkbox) => {
      checkbox.addEventListener("change", this.onOtherCheckboxChange.bind(this));
    });

    this.container.querySelectorAll(".other-input").forEach((input) => {
      input.addEventListener("change", this.onOtherTextInputChange.bind(this));
    });

    this.lastSaved = JSON.stringify(this.collectAllergens());
    this.bindWarnUnsavedChanges();
  }

  bindWarnUnsavedChanges() {
    if (!this.warnUnsavedChanges) return;

    this.container.setAttribute(WARN_UNSAVED_ATTR, "");
    bindUnsavedAllergenPartialReplace();
    this.syncWarnUnsavedChanges();
  }

  syncWarnUnsavedChanges() {
    if (!this.warnUnsavedChanges) return;

    syncUnsavedAllergenState();
  }

  onCheckboxChange({ target: checkbox }) {
    const { allergen, defaultSelection } = checkbox.dataset;

    const subcategories = this.container.querySelectorAll(
      `.subcategory-container[data-major-allergen="${allergen}"]`
    );
    if (checkbox.checked) {
      subcategories.forEach((el) => {
        el.classList.remove("is-hidden");
        if (defaultSelection) {
          el.querySelector(`[data-allergen="${defaultSelection}"]`).checked = true;
        }
        el.querySelectorAll(".other-inputs").forEach((otherContainer) => {
          const hasText = Array.from(otherContainer.querySelectorAll('input[type="text"]')).some(
            (i) => i.value.trim() !== ""
          );
          if (hasText) {
            otherContainer.querySelector(".other-checkbox").checked = true;
          }
        });
      });
    } else {
      subcategories.forEach((el) => {
        el.classList.add("is-hidden");
        el.querySelectorAll('input[type="checkbox"]').forEach((el) => (el.checked = false));
      });
    }

    this.commit(this.collectAllergens());
  }

  onOtherCheckboxChange({ target: checkbox }) {
    if (!checkbox.checked) {
      const otherContainer = checkbox.closest(".other-inputs");
      otherContainer.querySelectorAll(".other-input").forEach((input) => (input.value = ""));
    }

    this.commit(this.collectAllergens());
  }

  onOtherTextInputChange({ target: input }) {
    const otherContainer = input.closest(".other-inputs");
    const otherCheckbox = otherContainer.querySelector('input[type="checkbox"]');
    const otherTextInputs = otherContainer.querySelectorAll('input[type="text"]');

    otherCheckbox.checked = Array.from(otherTextInputs).some((i) => i.value.trim() !== "");

    this.commit(this.collectAllergens());
  }

  collectAllergens() {
    return AllergenInput.collect(this.container);
  }

  // Editing never shows the warning; commit only clears or refreshes it. Callers show via showIncompleteWarning.
  commit(allergens) {
    const incomplete = AllergenInput.incompleteMajors(this.container);
    if (incomplete.length === 0 || AllergenInput.warningVisible(this.container)) {
      AllergenInput.setWarnings(this.container, incomplete);
    }

    const serialized = JSON.stringify(allergens);
    if (serialized !== this.lastSaved) {
      this.lastSaved = serialized;
      this.onUpdate(allergens);
    }

    this.syncWarnUnsavedChanges();
  }

  static collect(container) {
    const allergens = {};

    container.querySelectorAll(".allergen-checkbox:checked").forEach((check) => {
      const { allergen } = check.dataset;
      if (requiresSubcategory(allergen)) {
        return;
      }
      allergens[allergen] = "true";
    });

    container.querySelectorAll(".other-checkbox:checked").forEach((check) => {
      const { otherKey, frenchOtherKey } = check.dataset;

      const otherContainer = check.closest(".other-inputs");
      const otherName = otherContainer
        .querySelector(`input[type="text"][data-lang="en"]`)
        .value.trim();
      const frenchName = otherContainer
        .querySelector(`input[type="text"][data-lang="fr"]`)
        .value.trim();

      if (otherName) {
        allergens[otherKey] = otherName;
      }
      if (frenchName) {
        allergens[frenchOtherKey] = frenchName;
      }
    });

    return allergens;
  }

  // Majors without a subtype are silently dropped by collect().
  static incompleteMajors(container) {
    const incomplete = [];
    container.querySelectorAll(".allergen-checkbox:checked").forEach((checkbox) => {
      const { allergen } = checkbox.dataset;
      if (!requiresSubcategory(allergen)) return;

      const subcategory = container.querySelector(
        `.subcategory-container[data-major-allergen="${allergen}"]`
      );
      if (!subcategory) return;

      if (!hasSubcategorySelection(subcategory)) incomplete.push(allergen);
    });
    return incomplete;
  }

  static incompleteMessage(incomplete) {
    const list =
      incomplete.length === 1
        ? incomplete[0]
        : incomplete.length === 2
          ? `${incomplete[0]} and ${incomplete[1]}`
          : `${incomplete.slice(0, -1).join(", ")}, and ${incomplete.at(-1)}`;
    const pronoun = incomplete.length === 1 ? "it" : "them";
    return `Pick a type for ${list}, or uncheck ${pronoun}.`;
  }

  static showIncompleteWarning(container) {
    const incomplete = AllergenInput.incompleteMajors(container);
    AllergenInput.setWarnings(container, incomplete);
    return incomplete.length > 0;
  }

  static scrollWarningIntoView(container) {
    const warning = warningElement(container);
    if (!warning) return;

    warning.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  static warningVisible(container) {
    const warning = warningElement(container);
    return !!warning && !warning.classList.contains("is-hidden");
  }

  static setWarnings(container, incomplete) {
    const warning = warningElement(container);
    if (!warning) return;
    if (incomplete.length === 0) {
      warning.classList.add("is-hidden");
      return;
    }
    const text = warning.querySelector("[data-allergen-summary-warning-text]");
    if (text) text.textContent = AllergenInput.incompleteMessage(incomplete);
    warning.classList.remove("is-hidden");
  }
}

export async function postAllergens(url, allergens) {
  postAndAppendScript(url, { allergens });
}

export async function postFacilityAllergens(url, allergens) {
  postAndAppendScript(url, { facility_allergens: allergens });
}

export function initToggles() {
  document.querySelectorAll("[data-toggle-button]").forEach((el) => {
    el.addEventListener("click", () => {
      const container = el.closest("[data-toggle-container]");

      container.querySelectorAll("[data-toggle-target]").forEach((target) => {
        const { toggleClass } = target.dataset;

        target.classList.toggle(toggleClass);
      });
    });
  });
}

// Read from the DOM to stay in sync with AllergenService.allergens_requiring_subcategory.
function subcategoryMajors() {
  if (subcategoryMajorsCache) return subcategoryMajorsCache;
  const el = document.querySelector("[data-subcategory-required-allergens]");
  if (el) {
    try {
      subcategoryMajorsCache = JSON.parse(el.dataset.subcategoryRequiredAllergens);
    } catch {
      subcategoryMajorsCache = null;
    }
  }
  return subcategoryMajorsCache || FALLBACK_SUBCATEGORY_MAJORS;
}

function requiresSubcategory(allergen) {
  return subcategoryMajors().includes(allergen);
}

function hasSubcategorySelection(subcategory) {
  return (
    !!subcategory.querySelector(".allergen-checkbox:checked") ||
    Array.from(subcategory.querySelectorAll(".other-input")).some((i) => i.value.trim() !== "")
  );
}

function warningElement(container) {
  return container.querySelector("[data-allergen-summary-warning]");
}

function bindUnsavedAllergenPartialReplace() {
  if (unsavedPartialReplaceBound) return;
  unsavedPartialReplaceBound = true;

  // The warn-unsaved scan reaps dirty inputs outside the replaced container, including this marker.
  document.addEventListener("partial:replace", syncUnsavedAllergenState);
}

function syncUnsavedAllergenState() {
  const warn = window.WarnUnsavedChanges;
  if (!warn) return;

  const anyIncomplete = Array.from(document.querySelectorAll(`[${WARN_UNSAVED_ATTR}]`)).some(
    (editor) => AllergenInput.incompleteMajors(editor).length > 0
  );
  if (anyIncomplete) {
    ensureUnsavedMarker();
    warn.markDirty(unsavedMarker, UNSAVED_INCOMPLETE_MESSAGE);
  } else if (unsavedMarker) {
    warn.markClean(unsavedMarker);
  }
}

function ensureUnsavedMarker() {
  if (!unsavedMarker) {
    unsavedMarker = document.createElement("input");
    unsavedMarker.type = "hidden";
  }
  if (!document.body.contains(unsavedMarker)) {
    document.body.appendChild(unsavedMarker);
  }
}
